import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from '../../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL, PAGE_SIZE } from '../../../constants';
import { StripedDataGrid } from '../../../styledComponents/datagrid';
import { renderCellExpand } from '../../../components/library/DataGrid/renderCellExpand';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  GridToolbarContainer,
  GridValueGetterParams,
  GridPagination,
  GridToolbarColumnsButton,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  Alert,
  Typography,
} from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import { Button, TextField } from '../../../components/library';
import { Autocomplete } from '@material-ui/lab';
import { PageWrapper } from '../../../styledComponents/wrappers';
import { useKeyPress } from '../../../utils/hooks/useKeyPress';
import { getZoneNameFromZone } from '../../catalog/utils/inventory/utils';
import { formatUserName } from '../utils/userUtils';
import { renderExports } from '../utils/exportUtils';
import {
  WarehouseZone as UtilsWarehouseZone,
  InventoryAdjustment,
  InventoryAdjustmentReason,
} from '../../../services/inventory/types';
import { FormatDateString, FormatDateTimeString } from '../../../utils/helper';

interface WarehouseZone extends UtilsWarehouseZone {
  shopifyLocationId: number;
  nsLocationId: number;
}

interface SearchFilters {
  searchTerm: string;
  adjustmentReasonId: number | null;
  warehouseZoneId: number | null;
  skuFilter: string[];
  dateFrom: string;
  dateTo: string;
}

export const INVENTORY_ADJUSTMENTS_ROUTE = '/inventory/adjustments';

const InventoryAdjustmentsList: React.FC = () => {
  const [searchInitiated, setSearchInitiated] = useState<boolean>(false);
  const [adjustments, setAdjustments] = useState<InventoryAdjustment[]>([]);
  const [adjustmentReasons, setAdjustmentReasons] = useState<InventoryAdjustmentReason[]>([]);
  const [warehouseZones, setWarehouseZones] = useState<WarehouseZone[]>([]);
  const [resultCount, setResultCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [filters, setFilters] = useState<SearchFilters>({
    searchTerm: '',
    adjustmentReasonId: null,
    warehouseZoneId: null,
    skuFilter: [],
    dateFrom: '',
    dateTo: '',
  });
  const [skuFilterInput, setSkuFilterInput] = useState<string>('');
  const [storeSearch, setStoreSearch] = useState<boolean>(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  const history = useHistory();

  const columns: GridColDef[] = [
    {
      headerName: 'ID',
      field: 'id',
      flex: 0.5,
    },
    {
      headerName: 'Warehouse Zone',
      field: 'warehouseZone',
      valueGetter: (params: GridValueGetterParams) =>
        getZoneNameFromZone(params.row.warehouseZone) || `Zone ${params.row.warehouseZoneId}`,
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      headerName: 'Reason',
      field: 'adjustmentReason',
      valueGetter: (params: GridValueGetterParams) => params.row.adjustmentReason?.code || '',
      renderCell: (params: GridRenderCellParams) => {
        const reason = params.row.adjustmentReason;
        if (!reason) return '';
        params.value = reason.code;
        return renderCellExpand(params, reason.description);
      },
      flex: 1,
    },
    {
      headerName: 'Exports',
      field: 'exports',
      renderCell: (params: GridRenderCellParams) => {
        const exports = params.row.inventoryAdjustmentExports || [];
        const exportContent = renderExports(exports);
        params.value = exportContent;
        return renderCellExpand(params);
      },
      flex: 1,
    },
    {
      headerName: 'Notes',
      field: 'notes',
      renderCell: renderCellExpand,
      flex: 3,
    },
    {
      headerName: 'Created',
      field: 'created',
      renderCell: (params: GridRenderCellParams) => {
        const hoverValue = FormatDateTimeString(params.value);
        params.value = FormatDateString(params.value);
        return renderCellExpand(params, hoverValue);
      },
      flex: 1,
    },
    {
      headerName: 'Created By',
      field: 'createdByUser',
      valueGetter: (params: GridValueGetterParams) =>
        formatUserName(params.row.createdByUser, params.row.createdByUserId),
      renderCell: (params: GridRenderCellParams) => {
        const user = params.row.createdByUser;
        if (!user) {
          params.value = `User ${params.row.createdByUserId}`;
          return renderCellExpand(params);
        }

        const displayName = user.name || user.email;
        const hoverValue = `${displayName} (${user.email})`;
        params.value = formatUserName(user, params.row.createdByUserId);
        return renderCellExpand(params, hoverValue);
      },
      flex: 1.5,
    },
  ];

  const fetchAdjustmentReasons = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/inventoryAdjustmentReasons`);
      setAdjustmentReasons((response as AxiosResponse<InventoryAdjustmentReason[]>).data);
    } catch (error) {
      console.error('Error fetching adjustment reasons:', error);
    }
  };

  const fetchWarehouseZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/zones`);
      setWarehouseZones((response as AxiosResponse<WarehouseZone[]>).data);
    } catch (error) {
      console.error('Error fetching warehouse zones:', error);
    }
  };

  const handleApiError = (error: any, context: string) => {
    console.error(`Error ${context}:`, error);

    if (error.response?.status === 414 || error.message?.includes('URI too long')) {
      setError(
        'The search parameters are too long. Please reduce the number of SKUs in your filter.',
      );
    } else if (error.response?.status === 400) {
      setError('Invalid search parameters. Please check your filters and try again.');
    } else if (error.response?.status >= 500) {
      setError('Server error occurred. Please try again later.');
    } else {
      setError(`An error occurred while ${context}. Please try again.`);
    }
  };

  const fetchData = useCallback(async () => {
    if (!searchInitiated) return;
    setLoading(true);
    setError(null);
    const sortBy = sortModel?.[0]?.field ?? 'created';
    const sortOrder = sortModel?.[0]?.sort?.toUpperCase() ?? 'DESC';

    try {
      const response = await axios.get(`${API_URL}/inventory/adjustments`, {
        params: {
          page: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          sort: sortBy,
          sortOrder,
          searchTerm: filters.searchTerm,
          adjustmentReasonId: filters.adjustmentReasonId,
          warehouseZoneId: filters.warehouseZoneId,
          skuFilter: filters.skuFilter.join(','),
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        },
      });

      const { adjustments, totalCount } = (response as AxiosResponse<{
        adjustments: InventoryAdjustment[];
        totalCount: number;
      }>).data;
      setAdjustments(adjustments);
      setResultCount(totalCount);
    } catch (error) {
      handleApiError(error, 'fetching inventory adjustments');
    } finally {
      setLoading(false);
      setSearchInitiated(false);
    }
  }, [filters, paginationModel, sortModel, searchInitiated]);

  const onRowClick = (params: GridRowParams) => {
    const { id } = params;
    history.push(`${INVENTORY_ADJUSTMENTS_ROUTE}/${id}`);
  };

  const generateCsvValue = (col: GridColDef, row: InventoryAdjustment) => {
    const params = { row, value: row[col.field as keyof InventoryAdjustment] };
    let value = '';

    if (col.valueGetter) {
      const result = col.valueGetter(params as GridValueGetterParams);
      if (col.valueFormatter && typeof result === 'object') {
        value = col.valueFormatter({ value: result } as GridValueFormatterParams);
      } else if (typeof result === 'object') {
        value = JSON.stringify(result);
      } else {
        value = String(result || '');
      }
    } else {
      value = String(params.value || '');
    }

    value = value.replace(/"/g, '""');
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      value = `"${value}"`;
    }
    return value;
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/inventory/adjustments`, {
        params: {
          page: 1,
          pageSize: 10000,
          sort: 'created',
          sortOrder: 'DESC',
          searchTerm: filters.searchTerm,
          adjustmentReasonId: filters.adjustmentReasonId,
          warehouseZoneId: filters.warehouseZoneId,
          skuFilter: filters.skuFilter.join(','),
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        },
      });

      const { adjustments: allAdjustments } = (response as AxiosResponse<{
        adjustments: InventoryAdjustment[];
        totalCount: number;
      }>).data;

      const visibleColumns = columns.filter(col => columnVisibilityModel[col.field] !== false);
      const headers = visibleColumns.map(col => col.headerName);
      const csvRows = [headers.join(',')];

      allAdjustments.forEach(row => {
        const values = visibleColumns.map(col => generateCsvValue(col, row));
        csvRows.push(values.join(','));
      });

      const csv = csvRows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drivetrain-inventory-adjustments-${
        new Date().toISOString().split('T')[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleApiError(error, 'exporting inventory adjustments');
    } finally {
      setLoading(false);
    }
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={handleExport}
          disabled={loading}
          size="small"
          ordinality="primary"
        >
          {loading ? 'Exporting...' : 'Export All'}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GridToolbarColumnsButton />
          <GridPagination sx={{ mx: 1 }} />
        </Box>
      </GridToolbarContainer>
    );
  }

  function CustomPagination() {
    return (
      <GridToolbarContainer sx={{ justifyContent: 'right' }}>
        <GridPagination sx={{ mx: 4 }} />
      </GridToolbarContainer>
    );
  }

  const processSkuFilter = (input: string) =>
    input
      ? input
          .split(',')
          .map(s => s.trim())
          .filter(s => s)
      : [];

  const handleFilterClear = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({
      searchTerm: '',
      adjustmentReasonId: null,
      warehouseZoneId: null,
      skuFilter: [],
      dateFrom: '',
      dateTo: '',
    });
    setSkuFilterInput('');
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      skuFilter: processSkuFilter(skuFilterInput),
    }));
    setSearchInitiated(true);
  };

  useEffect(() => {
    fetchAdjustmentReasons();
    fetchWarehouseZones();
    const storedColumns = window.localStorage.getItem('inventoryAdjustmentsSearchColumns');
    if (storedColumns) {
      setColumnVisibilityModel(JSON.parse(storedColumns));
    }

    const storedSearch = window.localStorage.getItem('inventoryAdjustmentsSearch');
    if (storedSearch) {
      setStoreSearch(true);
      const parsedFilters = JSON.parse(storedSearch);
      setFilters(parsedFilters);
      setSkuFilterInput(parsedFilters.skuFilter.join(', '));
      setSearchInitiated(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, paginationModel, sortModel, searchInitiated]);

  useEffect(() => {
    if (storeSearch) {
      window.localStorage.setItem('inventoryAdjustmentsSearch', JSON.stringify(filters));
    } else {
      window.localStorage.removeItem('inventoryAdjustmentsSearch');
    }
  }, [storeSearch, filters]);

  useEffect(() => {
    window.localStorage.setItem(
      'inventoryAdjustmentsSearchColumns',
      JSON.stringify(columnVisibilityModel),
    );
  }, [columnVisibilityModel]);

  useKeyPress({ activationKey: 'enter' }, () => setSearchInitiated(true));

  return (
    <PageWrapper>
      <Typography variant="h4" gutterBottom>
        Inventory Adjustments
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Grid container direction="row" spacing={2} justifyContent="start">
        <Grid container item xs={8} spacing={1}>
          <Grid container item xs={12} md={6} spacing={1}>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                id="searchTerm"
                value={filters.searchTerm}
                onChange={e =>
                  setFilters({
                    ...filters,
                    searchTerm: e.target.value,
                  })
                }
                placeholder="Search by notes"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Autocomplete
                size="small"
                id="adjustmentReasonId"
                options={adjustmentReasons}
                getOptionLabel={option => `${option.code} - ${option.description}`}
                value={adjustmentReasons.find(r => r.id === filters.adjustmentReasonId) || null}
                onChange={(_, value) =>
                  setFilters({
                    ...filters,
                    adjustmentReasonId: value?.id || null,
                  })
                }
                renderInput={params => (
                  <TextField {...params} label="Adjustment Reason" variant="outlined" />
                )}
              />
            </Grid>
          </Grid>

          <Grid container item xs={12} md={6} spacing={1}>
            <Grid item xs={12} lg={6}>
              <Autocomplete
                size="small"
                id="warehouseZoneId"
                options={warehouseZones}
                getOptionLabel={option => option.name}
                value={warehouseZones.find(z => z.id === filters.warehouseZoneId) || null}
                onChange={(_, value) =>
                  setFilters({
                    ...filters,
                    warehouseZoneId: value?.id || null,
                  })
                }
                renderInput={params => (
                  <TextField {...params} label="Warehouse Zone" variant="outlined" />
                )}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                id="skuFilter"
                label="SKU Filter"
                variant="outlined"
                value={skuFilterInput}
                onChange={e => setSkuFilterInput(e.target.value)}
                placeholder="e.g., SKU001, SKU002, SKU003"
                helperText={`Separated by commas (${filters.skuFilter.length} SKUs)`}
              />
            </Grid>
          </Grid>

          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                id="dateFrom"
                label="Date From"
                type="date"
                value={filters.dateFrom}
                onChange={e =>
                  setFilters({
                    ...filters,
                    dateFrom: e.target.value,
                  })
                }
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                id="dateTo"
                label="Date To"
                type="date"
                value={filters.dateTo}
                onChange={e =>
                  setFilters({
                    ...filters,
                    dateTo: e.target.value,
                  })
                }
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          item
          xs={4}
          spacing={1}
          direction="column"
          justifyContent="start"
          alignContent="end"
        >
          <Grid item>
            <Button variant="contained" onClick={handleFilterSubmit} ordinality="primary">
              Search
            </Button>
          </Grid>

          <Grid item>
            <Button variant="outlined" onClick={handleFilterClear} ordinality="secondary">
              Clear
            </Button>
          </Grid>

          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={storeSearch}
                  onChange={e => setStoreSearch(e.target.checked)}
                  color="primary"
                />
              }
              label="Remember Search"
            />
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        {loading && !adjustments.length ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <StripedDataGrid
            rows={adjustments}
            rowHeight={40}
            rowCount={resultCount}
            loading={loading}
            onRowClick={onRowClick}
            disableDensitySelector
            disableColumnFilter
            disableColumnMenu
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={model => {
              setPaginationModel(model);
              setSearchInitiated(true);
            }}
            pageSizeOptions={[PAGE_SIZE, PAGE_SIZE * 2, PAGE_SIZE * 4]}
            sortingMode="server"
            sortingOrder={['desc', 'asc', null]}
            sortModel={sortModel}
            onSortModelChange={model => {
              setSortModel(model);
              setSearchInitiated(true);
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            columns={columns}
            slots={{
              toolbar: CustomToolbar,
              footer: CustomPagination,
            }}
          />
        )}
      </Box>
    </PageWrapper>
  );
};

export default InventoryAdjustmentsList;
